import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Badge, Card, Col, Form, Row } from "react-bootstrap";
import { Box, FormControl, InputLabel, MenuItem, Select, Switch, TextField } from "@mui/material";
import { mdiCheckCircle, mdiKeyboardReturn } from "@mdi/js";
import { Controller, useForm } from "react-hook-form";

import { endpointPrincipal } from "../../../../../api/endPoints/endpointPrincipal/endpointPrincipal";

import { CriacaoEdicaoUsuarioInterface } from "../../../../../types/UsuarioInterface";

import { Button as ButtonCustom } from "../../../../../components/Formularios/Buttons/Button";
import ModalLoading from "../../../../../components/Formularios/Modal/ModalLoading";
import { SelectEmpresas } from "./components/SelectEmpresas";
import { SelectFiliais } from "./components/SelectFiliais";

// Estendendo a interface para incluir os novos campos
interface FormUsuarioExtendido extends CriacaoEdicaoUsuarioInterface {
  funcao?: string;
  grupo?: string;
  id_filial?: number | null;
}

interface FormUsuarioProps {
  idUsuario?: string;
  acao?: string;
}

const FormUsuario = ({ idUsuario, acao }: FormUsuarioProps) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormUsuarioExtendido>({});

  const senhaValidada = watch("senha");
  const isAgente = watch("backoffice");
  const empresaSelecionada = watch("id_empresa");
  const navigate = useNavigate();
  // const token = getSessionData("xc-helpdesk-token");

  // Define se está no modo de visualização
  const isVisualizando = acao === 'Visualizar';

  const isNovoUsuario = idUsuario === "novo";

  const [loading, setLoading] = useState<boolean>(false);
  const [funcoes, setFuncoes] = useState<any[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);

  const carregarOpcoesSelect = async () => {
    try {
      const [funcoesResponse, gruposResponse] = await Promise.all([
        endpointPrincipal.get('codigosistema/detalhes/FUNCOES'),
        endpointPrincipal.get('codigosistema/detalhes/GRUPOS')
      ]);
      
      setFuncoes(funcoesResponse.data);
      setGrupos(gruposResponse.data);
    } catch (error) {
      toast.error("Falha ao carregar opções dos campos. Motivo: " + error);
    }
  };

  const carregarUsuarioExistentePorId = async () => {
    setLoading(true);
    try {
      await carregarOpcoesSelect();
      const { data } = await endpointPrincipal.get(`/usuarios/${idUsuario}`);
      
      // Extrair os valores de funcao e grupo do objeto agente, se existir
      const dadosFormatados = { ...data };
      
      if (data.agente) {
        dadosFormatados.funcao = data.agente.funcao;
        dadosFormatados.grupo = data.agente.grupo;
        // Remover o objeto agente para evitar que seja enviado na requisição PUT
        delete dadosFormatados.agente;
      }
      
      console.log('Dados formatados para o formulário:', dadosFormatados);
      reset(dadosFormatados);
    } catch (error) {
      toast.error("Falha ao carregar dados. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNovoUsuario) {
      carregarUsuarioExistentePorId();
    } else {
      carregarOpcoesSelect();
    }
  }, []);

  const filialParaEnvio = (valor: FormUsuarioExtendido["id_filial"] | ""): number | null => {
    if (valor === "" || valor === null || valor === undefined) return null;
    const numero = typeof valor === "number" ? valor : parseInt(String(valor), 10);
    return Number.isNaN(numero) ? null : numero;
  };

  const atualizarUsuarioPorId = async (data: FormUsuarioExtendido) => {
    setLoading(true);
    try {
      // Criar uma cópia dos dados e remover o objeto agente, se existir
      const dadosParaEnvio = { ...data, id_filial: filialParaEnvio(data.id_filial) };
      if ('agente' in dadosParaEnvio) {
        delete dadosParaEnvio.agente;
      }
      
      console.log('Dados para atualização:', dadosParaEnvio);
      await endpointPrincipal.put(`/usuarios/${idUsuario}`, dadosParaEnvio);
      toast.success(`Usuario editado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error("Falha ao alterar Usuario. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  const incluirNovoUsuario = async (data: FormUsuarioExtendido) => {
    setLoading(true);
    try {
      // Criar uma cópia dos dados e remover o objeto agente, se existir
      const dadosParaEnvio = { ...data, id_filial: filialParaEnvio(data.id_filial) };
      if ('agente' in dadosParaEnvio) {
        delete dadosParaEnvio.agente;
      }
      
      console.log('Dados para inclusão:', dadosParaEnvio);
      await endpointPrincipal.post("/usuarios", dadosParaEnvio);
      toast.success(`Usuario adicionado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error(`Erro ao adicionar Usuario`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (usuario: FormUsuarioExtendido) => {
    if (isNovoUsuario) {
      incluirNovoUsuario(usuario);
    } else {
      atualizarUsuarioPorId(usuario);
    }
  };

  return (
    <>
      <Card>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Card.Body>
            <Box>
              <Row>
                <Col sm={12} md={12} lg={12}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    label="Nome"
                    size="small"
                    {...register("nome", { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isVisualizando}
                  />
                </Col>
              </Row>

              <Row>
                <Col sm={12} md={12} lg={4}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    label="Usuário"
                    size="small"
                    {...register("nome_usuario", { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isVisualizando}
                  />
                </Col>
                <Col sm={12} md={12} lg={4}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    label="E-mail"
                    size="small"
                    {...register("email", {
                      required: true,
                      setValueAs: (valor: string) =>
                        typeof valor === "string" ? valor.trim() : valor,
                    })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isVisualizando}
                  />
                </Col>
                <Col sm={12} md={12} lg={4}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    label="Telefone"
                    size="small"
                    {...register("telefone", { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isVisualizando}
                  />
                </Col>
              </Row>

              <Row>
                {isNovoUsuario && (
                  <Col sm={12} md={12} lg={4}>
                    <TextField
                      sx={{ width: "100%", mb: 3 }}
                      label="Senha"
                      size="small"
                      type="password"
                      {...register("senha", { required: true, minLength: 5 })}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    {errors.senha && (
                      <Badge bg="danger" className="mb-2">
                        A senha é obrigatória e deve ter pelo menos 5
                        caracteres.
                      </Badge>
                    )}
                  </Col>
                )}
                <Col sm={12} md={12} lg={4}>
                  <TextField
                    sx={{ width: "100%", mb: 3 }}
                    label="CPF"
                    size="small"
                    {...register("cpf", {
                      setValueAs: (valor: string) =>
                        typeof valor === "string" ? valor.trim() : valor,
                      validate: (valor) => {
                        const digitos = (valor ?? "").trim().replace(/\D/g, "");
                        if (digitos.length === 0) return true;
                        return (
                          digitos.length === 11 ||
                          "Informe um CPF com 11 dígitos ou deixe o campo em branco."
                        );
                      },
                    })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isVisualizando}
                    error={!!errors.cpf}
                  />
                  {errors.cpf && (
                    <Badge bg="danger" className="mb-2">
                      {errors.cpf.message}
                    </Badge>
                  )}
                </Col>
                <Col sm={12} md={12} lg={4}>
                  <SelectEmpresas
                    name="id_empresa"
                    control={control}
                    label="Empresa"
                    disabled={isVisualizando}
                  />
                </Col>
                <Col sm={12} md={12} lg={4}>
                  <SelectFiliais
                    name="id_filial"
                    control={control}
                    label="Filial (opcional)"
                    idEmpresa={empresaSelecionada}
                    disabled={isVisualizando}
                  />
                </Col>
              </Row>

              {isAgente === 1 && (
                <Row>
                  <Col xs={12} sm={6} md={6} lg={6}>
                    <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                      <InputLabel>Função</InputLabel>
                      <Controller
                        name="funcao"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Função"
                            disabled={isVisualizando}
                          >
                            <MenuItem value="">Selecione...</MenuItem>
                            {funcoes.map((funcao) => (
                              <MenuItem key={funcao.codigo} value={funcao.codigo}>
                                {funcao.descricao}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Col>
                  <Col xs={12} sm={6} md={6} lg={6}>
                    <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                      <InputLabel>Grupo</InputLabel>
                      <Controller
                        name="grupo"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Grupo"
                            disabled={isVisualizando}
                          >
                            <MenuItem value="">Selecione...</MenuItem>
                            {grupos.map((grupo) => (
                              <MenuItem key={grupo.codigo} value={grupo.codigo}>
                                {grupo.descricao}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Col>
                </Row>
              )}

              <Row>
                <Col xs={12} sm={12} md={12} lg={12}>
                  <div className="d-flex align-items-center flex-wrap">
                    <Col xs={6} sm={6} md={6} lg={3}>
                      <div className="d-flex align-items-center mb-1">
                        <label htmlFor="" className="form-label me-2 mb-0">
                          Ativo
                        </label>
                        <Controller
                          name="situacao"
                          control={control}
                          defaultValue={1}
                          render={({ field }) => (
                            <Switch
                              checked={field.value === 1}
                              onChange={(e) =>
                                field.onChange(e.target.checked ? 1 : 0)
                              }
                              disabled={isVisualizando ? true : false}
                            />
                          )}
                        />
                      </div>
                    </Col>
                    <Col xs={6} sm={6} md={6} lg={3}>
                      <div className="d-flex align-items-center mb-1">
                        <label htmlFor="" className="form-label me-2 mb-0">
                          É Agente?
                        </label>
                        <Controller
                          name="backoffice"
                          control={control}
                          defaultValue={0}
                          render={({ field }) => (
                            <Switch
                              checked={field.value === 1}
                              onChange={(e) =>
                                field.onChange(e.target.checked ? 1 : 0)
                              }
                              disabled={isVisualizando ? true : false}
                            />
                          )}
                        />
                      </div>
                    </Col>
                    <Col xs={12} sm={6} md={6} lg={3}>
                      <div className="d-flex align-items-center mb-1">
                        <label htmlFor="" className="form-label me-2 mb-0">
                          Ver dados da empresa?
                        </label>
                        <Controller
                          name="responsavelEmpresa"
                          control={control}
                          defaultValue={0}
                          render={({ field }) => (
                            <Switch
                              checked={field.value === 1}
                              onChange={(e) =>
                                field.onChange(e.target.checked ? 1 : 0)
                              }
                              disabled={isVisualizando ? true : false}
                            />
                          )}
                        />
                      </div>
                    </Col>
                  </div>
                </Col>
              </Row>
            </Box>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex my-2 gap-1 justify-content-between">
              <ButtonCustom
                type="button"
                className="btn btn-outline-secondary rounded-2 d-flex align-items-center gap-1"
                descricaoBotao="Voltar"
                icone={mdiKeyboardReturn}
                onclick={() => {
                  navigate(-1);
                }}
              />
              <ButtonCustom
                type="submit"
                className="btn btn-success rounded-2 d-flex align-items-center gap-1"
                descricaoBotao="Salvar"
                icone={mdiCheckCircle}
                disabled={isVisualizando}
              />
            </div>
          </Card.Footer>
        </Form>
      </Card>
      <ModalLoading show={loading} label="Carregando dados ..." />
    </>
  );
};
export default FormUsuario;
